import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import EinsatzDetail from './pages/EinsatzDetail';
import TopAppBar from './components/AppBar';
import { offlineStorage } from './utils/offlineStorage';
import { isAuthenticatedSync, initializeOnlineOfflineHandlers } from './utils/auth';
import { TutorialProvider, useTutorial } from './contexts/TutorialContext';
import TutorialOverlay from './components/tutorial/TutorialOverlay';
import { getTutorialManager } from './utils/tutorialManager';
import { TourProvider, useTour } from './contexts/TourContext';
import TourOverlay from './components/tour/TourOverlay';
import { getTourManager } from './utils/tourManager';
import { getCacheManager } from './utils/cacheManager';

// Protected Route Component - prüft Authentifizierung und rendert die richtige Komponente
const ProtectedRoute: React.FC<{ einsatzleiterMode?: boolean; setEinsatzleiterMode?: React.Dispatch<React.SetStateAction<boolean>> }> = ({ 
  einsatzleiterMode = false, 
  setEinsatzleiterMode = () => {} 
}) => {
  const navigate = useNavigate();
  const { startTutorial } = useTutorial();
  const { startTour } = useTour();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  useEffect(() => {
    const checkAuthAndNavigate = async () => {
      setIsLoading(true);
      
      // Prüfe ob Benutzer eingeloggt ist
      if (isAuthenticatedSync()) {
        console.log('✅ Benutzer ist eingeloggt');
        setIsAuthenticated(true);
        
        // Nur für die Root-Route (/) Tutorial-Logik ausführen
        const currentPath = window.location.pathname;
        if (currentPath === '/') {
          console.log('🏠 Root-Route - führe Tutorial-Logik aus');
          
          // Cache bei Organisationswechsel löschen
          const checkAndClearCache = async () => {
            try {
              const cacheManager = getCacheManager();
              await cacheManager.checkAndClearCacheOnOrgChange();
            } catch (error) {
              console.error('Fehler beim Cache-Check:', error);
            }
          };

          // Prüfe ob Tutorial benötigt wird
          const checkAndStartTutorial = async () => {
            try {
              // Prüfe zuerst den Flag aus der Login-Seite
              const tutorialNeeded = localStorage.getItem('tutorialNeeded');
              console.log('🔍 Tutorial-Flag aus Login:', tutorialNeeded);
              
              if (tutorialNeeded === 'true') {
                console.log('🎯 Tutorial-Flag gesetzt - starte automatisch');
                console.log('🚀 Rufe startTutorial() auf...');
                startTutorial();
                console.log('✅ startTutorial() wurde aufgerufen');
                // Flag löschen, damit es nicht nochmal startet
                localStorage.removeItem('tutorialNeeded');
                // Bleibe auf der aktuellen Route, Tutorial-Overlay wird angezeigt
                // Warte kurz, damit das Tutorial starten kann
                setTimeout(async () => {
                  console.log('⏰ Warte 1 Sekunde, dann zum Dashboard...');
                  
                  // Nach dem Tutorial: Prüfe ob Tour benötigt wird
                  try {
                    const tourManager = getTourManager();
                    const needsTour = await tourManager.checkIfTourNeeded();
                    
                    if (needsTour) {
                      console.log('🎯 Tour wird benötigt - starte automatisch');
                      console.log('🚀 Rufe startTour() auf...');
                      startTour();
                      console.log('✅ startTour() wurde aufgerufen');
                    }
                  } catch (error) {
                    console.error('Fehler beim Tour-Check nach Tutorial:', error);
                  }
                  
                  navigate('/dashboard');
                }, 1000);
                return;
              }

              // Fallback: Prüfe nochmal mit dem Tutorial-Manager
              const tutorialManager = getTutorialManager();
              const needsTutorial = await tutorialManager.checkIfTutorialNeeded();
              
              if (needsTutorial) {
                console.log('🎯 Tutorial wird benötigt - starte automatisch');
                console.log('🚀 Rufe startTutorial() auf...');
                startTutorial();
                console.log('✅ startTutorial() wurde aufgerufen');
                // Bleibe auf der aktuellen Route, Tutorial-Overlay wird angezeigt
                // Warte kurz, damit das Tutorial starten kann
                setTimeout(async () => {
                  console.log('⏰ Warte 1 Sekunde, dann zum Dashboard...');
                  
                  // Nach dem Tutorial: Prüfe ob Tour benötigt wird
                  try {
                    const tourManager = getTourManager();
                    const needsTour = await tourManager.checkIfTourNeeded();
                    
                    if (needsTour) {
                      console.log('🎯 Tour wird benötigt - starte automatisch');
                      console.log('🚀 Rufe startTour() auf...');
                      startTour();
                      console.log('✅ startTour() wurde aufgerufen');
                    }
                  } catch (error) {
                    console.error('Fehler beim Tour-Check nach Tutorial:', error);
                  }
                  
                  navigate('/dashboard');
                }, 1000);
              } else {
                console.log('✅ Tutorial nicht benötigt - leite zum Dashboard weiter');
                navigate('/dashboard');
              }
            } catch (error) {
              console.error('Fehler beim Tutorial-Check:', error);
              // Bei Fehlern zum Dashboard weiterleiten
              navigate('/dashboard');
            }
          };
          
          // Erst Cache prüfen, dann Tutorial
          await checkAndClearCache();
          checkAndStartTutorial();
        } else {
          console.log('📍 Spezifische Route - prüfe Tour-Flag:', currentPath);
          
          // Tour nur starten wenn Flag vom Login gesetzt wurde
          const tourNeeded = localStorage.getItem('tourNeeded');
          console.log('🔍 Tour-Flag Wert:', tourNeeded);
          if (tourNeeded === 'true') {
            console.log('🎯 Tour-Flag gesetzt - starte Tour');
            startTour();
            localStorage.removeItem('tourNeeded'); // Flag löschen
          } else {
            console.log('✅ Kein Tour-Flag - prüfe Tour-Status direkt');
            
            // Fallback: Wenn kein Flag gesetzt ist, prüfe Tour-Status direkt
            const checkTourDirectly = async () => {
              try {
                console.log('🔍 App: Prüfe Tour-Status direkt...');
                const { getTourManager } = await import('./utils/tourManager');
                const tourManager = getTourManager();
                const needsTour = await tourManager.checkIfTourNeeded();
                
                console.log('🔍 App: Tour-Status-Check Ergebnis:', needsTour);
                
                if (needsTour) {
                  console.log('🎯 App: Tour wird benötigt - starte direkt');
                  startTour();
                } else {
                  console.log('✅ App: Tour nicht benötigt');
                }
              } catch (error) {
                console.error('Fehler beim direkten Tour-Check:', error);
              }
            };
            
            checkTourDirectly();
          }
        }
      } else {
        console.log('❌ Benutzer ist nicht eingeloggt - zeige Login');
        setIsAuthenticated(false);
        navigate('/login');
      }
      
      setIsLoading(false);
    };

    checkAuthAndNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, startTutorial]);
  
  // Zeige Loading während der Authentifizierungsprüfung
  if (isLoading) {
    return <div>Lade...</div>;
  }
  
  // Wenn nicht authentifiziert, zeige nichts (wird zur Login-Seite weitergeleitet)
  if (!isAuthenticated) {
    return null;
  }
  
  // Wenn authentifiziert, rendere die richtige Komponente basierend auf der Route
  const currentPath = window.location.pathname;
  
  if (currentPath === '/dashboard') {
    return (
      <>
        <Dashboard />
        <TourOverlay />
      </>
    );
  } else if (currentPath.startsWith('/einsatz/')) {
    return (
      <>
        <EinsatzDetail einsatzleiterMode={einsatzleiterMode} setEinsatzleiterMode={setEinsatzleiterMode} />
        <TourOverlay />
      </>
    );
  } else {
    // Für alle anderen Routen (wie '/') zum Dashboard weiterleiten
    navigate('/dashboard');
    return <div>Leite zum Dashboard weiter...</div>;
  }
};



const App: React.FC = () => {
  const [einsatzleiterMode, setEinsatzleiterMode] = useState(false);

  useEffect(() => {
    // Token-Manager immer initialisieren - der Interceptor handhabt die Authentifizierung
    const initTokenManager = async () => {
      try {
        const { getTokenManager } = await import('./utils/tokenManager');
        getTokenManager().initialize();
        console.log('✅ Token-Manager initialisiert');
      } catch (error) {
        console.error('Failed to initialize token manager:', error);
      }
    };

    initTokenManager();

    // Tutorial-Check nur durchführen, wenn der Benutzer eingeloggt ist
    const checkTutorial = async () => {
      try {
        // Prüfe ob der Benutzer eingeloggt ist
        const { isAuthenticatedSync } = await import('./utils/auth');
        if (isAuthenticatedSync()) {
          const tutorialManager = getTutorialManager();
          const needsTutorial = await tutorialManager.checkIfTutorialNeeded();
          
          if (needsTutorial) {
            console.log('🎯 Tutorial wird benötigt - starte automatisch');
            // Tutorial wird automatisch gestartet, wenn der Benutzer eingeloggt ist
          } else {
            console.log('✅ Tutorial nicht benötigt - alle Daten vorhanden');
          }
        } else {
          console.log('🔒 Benutzer nicht eingeloggt - überspringe Tutorial-Check');
        }
      } catch (error) {
        console.error('Fehler beim Tutorial-Check:', error);
      }
    };

    checkTutorial();

    // Cache-Manager für Multi-Tenancy initialisieren
    const initCacheManager = async () => {
      try {
        const cacheManager = getCacheManager();
        await cacheManager.checkAndClearCacheOnOrgChange();
        console.log('✅ Cache-Manager initialisiert');
      } catch (error) {
        console.error('Fehler beim Cache-Manager:', error);
      }
    };

    initCacheManager();

    // Offline Storage initialisieren
    offlineStorage.initialize().catch(error => {
      console.error('Failed to initialize offline storage:', error);
    });

    // Online/Offline-Event-Handler initialisieren
    initializeOnlineOfflineHandlers();

    // Cleanup beim Unmount
    return () => {
      // Cleanup will be handled by the tokenManager instance
    };
  }, []);

  // Separate effect for service worker manager to avoid circular dependencies
  useEffect(() => {
    // Defer service worker initialization to avoid circular dependency issues
    const initServiceWorker = async () => {
      try {
        const { getServiceWorkerManager } = await import('./utils/serviceWorkerManager');
        const swManager = getServiceWorkerManager();
        
        // Service Worker Manager initialisieren und Notification-Permission anfordern
        swManager.requestNotificationPermission();
        
        // Force update service worker to ensure latest configuration
        swManager.forceUpdate();
      } catch (error) {
        console.error('Failed to initialize service worker manager:', error);
      }
    };

    // Use setTimeout to ensure this runs after the initial render cycle
    setTimeout(initServiceWorker, 100);
  }, []);

  // Kein Login-Check mehr nötig - der Interceptor handhabt alles automatisch
  // Wenn ein Benutzer direkt zur Login-Seite navigiert, soll er dort bleiben

  return (
    <TutorialProvider>
      <TourProvider>
        <Router>
          <TopAppBar einsatzleiterMode={einsatzleiterMode} />
          <Routes>
            <Route path="/" element={<ProtectedRoute einsatzleiterMode={einsatzleiterMode} setEinsatzleiterMode={setEinsatzleiterMode} />} />
                 <Route path="/login" element={<Login />} />
            <Route path="/dashboard" element={<ProtectedRoute einsatzleiterMode={einsatzleiterMode} setEinsatzleiterMode={setEinsatzleiterMode} />} />
            <Route path="/einsatz/:id" element={<ProtectedRoute einsatzleiterMode={einsatzleiterMode} setEinsatzleiterMode={setEinsatzleiterMode} />} />
          </Routes>
          <TutorialOverlay />
        </Router>
      </TourProvider>
    </TutorialProvider>
  );
};

export default App;
